import { FC, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CaretLeftIcon } from '@/atoms/Icons/CaretLeftIcon'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { BasicCarousel } from '@/molecules/BasicCarousel'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { HomesteadParagraphSM, HomesteadTitleLG } from '../components/Typography'
import { prizeItemsList } from './PrizeItemsTab'

const showImages = [
  {
    id: 1,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952139/angel-studios/guild/homestead/homestead-img-01.webp',
    alt: 'Homestead Images',
  },
  {
    id: 2,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952142/angel-studios/guild/homestead/homestead-img-04.webp',
    alt: 'Homestead Images',
  },
  {
    id: 3,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952141/angel-studios/guild/homestead/homestead-img-02.webp',
    alt: 'Homestead Images',
  },
  {
    id: 4,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952142/angel-studios/guild/homestead/homestead-img-07.webp',
    alt: 'Homestead Images',
  },
  {
    id: 5,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952143/angel-studios/guild/homestead/homestead-img-08.webp',
    alt: 'Homestead Images',
  },
  {
    id: 6,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952144/angel-studios/guild/homestead/homestead-img-09.webp',
    alt: 'Homestead Images',
  },
  {
    id: 7,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952145/angel-studios/guild/homestead/homestead-img-05.webp',
    alt: 'Homestead Images',
  },
  {
    id: 8,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952145/angel-studios/guild/homestead/homestead-img-10.webp',
    alt: 'Homestead Images',
  },
  {
    id: 9,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952151/angel-studios/guild/homestead/homestead-img-06.webp',
    alt: 'Homestead Images',
  },
  {
    id: 10,
    src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952148/angel-studios/guild/homestead/homestead-img-03.webp',
    alt: 'Homestead Images',
  },
]

interface Props {
  scrollToProducts: () => void
  displayStickyBtn: boolean
  scrollToPrizes: () => void
}

export const PresaleDetailsTab: FC<Props> = ({ scrollToProducts, displayStickyBtn, scrollToPrizes }) => {
  const { t } = useTranslate('tickets')
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index)
    setIsLightboxOpen(true)
  }

  return (
    <FlexColumn className="container mx-auto mb-16 gap-16 px-4 pt-16 md:gap-[72px] md:px-16 md:pb-32 lg:px-24">
      <div className="flex flex-col gap-1">
        <Text color="homestead-primary-blue" className="homestead-title-lg md:homestead-title-xl uppercase md:hidden">
          {t('preOrderNow', 'Pre-Order Now. Choose Showtimes Later.')}
        </Text>
        <div
          id="youtube"
          className="my-3 max-h-[320px] !w-full max-w-[530px] overflow-hidden rounded-[10px] md:mb-[72px]"
        >
          <EmbeddedVideo
            url="https://www.youtube-nocookie.com/embed/V5zQ6XyHhrs"
            className="mx-auto  !rounded-[10px] md:mx-0"
          />
        </div>
        <Text color="homestead-primary-blue" className="homestead-title-xl mb-4 hidden uppercase md:block">
          {t('preOrderNow', 'Pre-Order Now. Choose Showtimes Later.')}
        </Text>
        <Text color="homestead-blue" className="homestead-paragraph-sm md:homestead-paragraph-md">
          <Translate t={t} i18nKey="preOrderNowBodyV6">
            Secure your tickets for Homestead today and automatically{' '}
            <b>enter for a chance to win a $300,000 Instant Homestead, including 1 Bitcoin!</b> This incredible prize
            offers ultimate peace of mind and self-sufficiency.
          </Translate>
        </Text>
        <Text color="homestead-blue" className="homestead-paragraph-sm md:homestead-paragraph-md mt-4">
          <Translate t={t} i18nKey="preOrderNowBodyV2_2">
            Don&apos;t miss out—pre-order now and prepare for an unforgettable adventure!
          </Translate>
        </Text>
      </div>
      <section>
        <FlexColumn className="gap-1 pb-6">
          <Text className="homestead-title-lg uppercase" color="homestead-primary-blue">
            {t('superfanBenefitItems', 'Superfan Benefit Items')}
          </Text>
          <Text className="homestead-paragraph-sm md:homestead-paragraph-md" color="homestead-blue">
            {t(
              'superfanBenefitItemsBody',
              'Below are some of the exclusive items available based on the backer pack you choose.',
            )}
          </Text>
        </FlexColumn>
        <FlexColumn className="gap-12 md:gap-16">
          <BonusItemCard
            badgeText="Digital Book"
            image="https://images.angelstudios.com/image/upload/v1730479729/book-cover-perspective-view-03.jpg"
            title={t('digitalBookTitle', 'Digital Book - Homestead: Black Autumn. Faith and Family Edition')}
            description={t(
              'digitalBookDescription',
              'Prepare for the film event in the best way you can, with the bestselling novel that started it all.',
            )}
          />
          <BonusItemCard
            badgeClassName="!bg-[#F0EEE7]"
            badgeText="Audio Book"
            image="https://images.angelstudios.com/image/upload/v1730479680/homestead-audio-book-cover-01.png"
            title={t('audioBookTitle', 'Audio Book - Homestead: Black Autumn. Faith and Family Edition')}
            description={t(
              'audioBookDescription',
              'Listen anywhere to the bestselling novel behind Homestead, and dive into the film’s world before you even pick up your popcorn.',
            )}
          />
          <BonusItemCard
            image="https://images.angelstudios.com/image/upload/v1725484357/name-in-credits-img-02.webp"
            title={t('nameInCreditsTitleV2', 'Your Name in the Credits for Streaming')}
            description={t(
              'nameInCreditsDescriptionV2',
              'Make your mark on Homestead’s release. Have your name featured in the credits—on our streaming platforms, DVD/Blu-ray, and alongside the community who brought this film to life.',
            )}
          />
          <BonusItemCard
            badgeClassName="!bg-[#F0EEE7]"
            badgeText="e-Book"
            image="https://images.angelstudios.com/image/upload/v1726153403/angel-studios/guild/homestead/homestead-prep-bundle-mock-01.webp"
            title={t('eBookBundleTitle', 'eBook Bundle - Homesteading & Preparedness Resource Library')}
            description={t(
              'eBookBundleDescription',
              'Enhance your library with the ultimate DIY homesteading and preparedness collection. This comprehensive eBook bundle equips you with essential skills for any just-in-case scenario.',
            )}
          />
          <GBFadeInExperiment experimentName="giveaway-remove-1000" defaultValue={false}>
            <ExperimentVariation variation={true}>{null}</ExperimentVariation>
            <ExperimentVariation default>
              <BonusItemCard
                image="https://images.angelstudios.com/image/upload/v1725484408/nmcd-red-carpet-image-01.webp"
                title={t('redCarpetPremiereTitle', 'Two Red Carpet Tickets to the Homestead Movie Premiere in Utah')}
                description={t(
                  'redCarpetPremiereDescription',
                  'Strut the red carpet like the film insider you are, and enjoy a once-in-a-lifetime experience when you attend the Utah premiere of Homestead!',
                )}
                disclaimer={t('redCarpetPremiereDisclaimer', 'Does not include travel, lodging, and food expenses.')}
              />
            </ExperimentVariation>
          </GBFadeInExperiment>
          <BonusItemCard
            image="https://images.angelstudios.com/image/upload/v1726078846/angel-studios/guild/homestead/homestead-site-01.webp"
            title={t('setVisitTitle', 'Invitation to Visit the Homestead Property During Filming')}
            description={t(
              'setVisitDescription',
              'Join the cast and crew of Homestead on-location in Utah during the filming of the TV series, with exclusive access for you and a guest.',
            )}
            disclaimer={t(
              'setVisitDisclaimer',
              'Does not include travel, lodging, and food expenses. Although you’ll have on-location access, we cannot promise interactions with the cast or crew.',
            )}
          />
        </FlexColumn>
      </section>
      <section>
        <HomesteadTitleLG className="mb-1">{t('grandPrizeItems', 'Grand Prize Items')}</HomesteadTitleLG>
        <HomesteadParagraphSM className="mb-6">
          {t('giveawayValue', 'This giveaway package is valued at $300,000.')}
        </HomesteadParagraphSM>
        <ul className="ml-5 list-[square]">
          {prizeItemsList.slice(0, 12).map((prizeItem, index) => (
            <li key={index} className="my-1 !text-homestead-blue">
              <HomesteadParagraphSM>
                {prizeItem.title} ({prizeItem.value})
              </HomesteadParagraphSM>
            </li>
          ))}
        </ul>
        <Button
          className="mt-6 w-full rounded-xl border-2 !border-homestead-blue md:w-fit"
          variant="white"
          outline
          onClick={scrollToPrizes}
        >
          <Text
            className="mb-1 !font-['bio-sans'] text-[16px] font-semibold uppercase leading-[120%]"
            color="homestead-blue"
          >
            Show all Items
          </Text>
        </Button>
      </section>

      <section className="flex flex-col gap-4">
        <Text color="homestead-primary-blue" className="homestead-title-lg uppercase">
          {t('showImages', 'Show Images')}
        </Text>
        <BasicCarousel showGradients containerClassName="!gap-2 !px-2">
          {showImages.map((image, index) => (
            <img
              key={image.id}
              src={image.src}
              alt={image.alt}
              className="w-72 rounded-lg"
              onClick={() => openLightbox(index)}
            />
          ))}
        </BasicCarousel>
        {isLightboxOpen && (
          <HomesteadLightBox
            showImages={showImages}
            currentImageIndex={currentImageIndex}
            setIsLightboxOpen={setIsLightboxOpen}
            setCurrentImageIndex={setCurrentImageIndex}
          />
        )}
      </section>

      {displayStickyBtn && <StickyButton scrollToProducts={scrollToProducts} />}
    </FlexColumn>
  )
}

interface BonusItemCardProps {
  title: string
  description: string
  image: string
  disclaimer?: string
  badgeText?: string
  badgeClassName?: string
  alt?: string
}

const BonusItemCard: FC<BonusItemCardProps> = ({
  title,
  description,
  image,
  alt,
  disclaimer,
  badgeText,
  badgeClassName,
}) => (
  <FlexColumn className="max-w-[480px] gap-4 self-center md:self-start">
    <div style={{ aspectRatio: 16 / 9 }} className="relative w-full">
      {badgeText && (
        <div
          className={classNames(
            'absolute right-4 top-4 z-10 rounded-md bg-[#D3D3D6] px-2 pt-0.5 pb-1 !font-[bio-sans] text-xs md:text-sm uppercase font-semibold',
            badgeClassName,
          )}
        >
          {badgeText}
        </div>
      )}
      <Image src={image} alt={alt} className="rounded-lg object-cover" fill />
    </div>
    <FlexColumn className="gap-1">
      <Text color="homestead-primary-blue" className="homestead-title-xs md:homestead-title-sm !font-semibold">
        {title}
      </Text>
      <Text color="homestead-blue" className="homestead-paragraph-sm md:homestead-paragraph-md">
        {description} <span className="italic"></span>
      </Text>
      {disclaimer && (
        <Text color="homestead-blue" className="homestead-paragraph-sm md:homestead-paragraph-md italic">
          <li className="ml-7 list-[square]">{disclaimer}</li>
        </Text>
      )}
    </FlexColumn>
  </FlexColumn>
)

interface StickyButtonProps {
  scrollToProducts: () => void
}

const StickyButton: FC<StickyButtonProps> = ({ scrollToProducts }) => {
  const [isVisible, setIsVisible] = useState(false)
  const { t } = useTranslate('tickets')

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsVisible(true)
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <div
      className={classNames('sticky bottom-0 pb-6 pt-4 z-20 transition-transform duration-500 ease-in-out md:hidden', {
        'translate-y-0 opacity-100': isVisible,
        'translate-y-10 opacity-0': !isVisible,
      })}
      style={{
        background: 'linear-gradient(180deg, rgba(245, 245, 245, 0.00) 0%, #F5F5F5 100%)',
      }}
    >
      <Button
        className="mx-auto w-[calc(100%-8px)] rounded-[14px] border-none !bg-homestead-red !py-[17px]"
        variant="core-gray-900"
        onClick={scrollToProducts}
      >
        <Text color="white" className="homestead-btn-md uppercase">
          {t('getTickets', 'Get Tickets')}
        </Text>
      </Button>
    </div>
  )
}

interface LightBoxProps {
  showImages: { src: string; alt: string }[]
  currentImageIndex: number
  setIsLightboxOpen: (isOpen: boolean) => void
  setCurrentImageIndex: (index: number) => void
}

const HomesteadLightBox: FC<LightBoxProps> = ({
  showImages,
  currentImageIndex,
  setIsLightboxOpen,
  setCurrentImageIndex,
}) => {
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  const closeLightbox = () => {
    setIsLightboxOpen(false)
  }

  const handleBackgroundClick = () => {
    closeLightbox()
  }

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }

  const goToPrevious = () => {
    const newIndex = currentImageIndex === 0 ? showImages.length - 1 : currentImageIndex - 1
    setCurrentImageIndex(newIndex)
  }

  const goToNext = () => {
    const newIndex = currentImageIndex === showImages.length - 1 ? 0 : currentImageIndex + 1
    setCurrentImageIndex(newIndex)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const swipeDistance = touchStart - touchEnd
    const swipeThreshold = 50

    if (swipeDistance > swipeThreshold) {
      goToNext()
    }

    if (swipeDistance < -swipeThreshold) {
      goToPrevious()
    }

    setTouchStart(null)
    setTouchEnd(null)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75"
      id="lightbox-background"
      onClick={handleBackgroundClick}
    >
      <div
        className="relative"
        onClick={handleContentClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <button className="absolute left-1 top-1/2 -translate-y-1/2 text-3xl lg:-left-7" onClick={goToPrevious}>
          <CaretLeftIcon size={24} />
        </button>

        <button className="absolute right-1 top-1/2 -translate-y-1/2 text-3xl lg:-right-7" onClick={goToNext}>
          <CaretRightIcon size={24} />
        </button>

        <img
          src={showImages[currentImageIndex].src}
          alt={showImages[currentImageIndex].alt}
          className="max-h-screen max-w-full rounded-lg shadow-lg lg:max-w-screen-lg"
        />
      </div>
    </div>
  )
}
